import React from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, BandPill, DataBarCell, Chip, ProvenanceFooter, ProvenanceBadge } from '../components/ui/kits.jsx';
import { HBars, Lollipop } from '../components/charts/charts.jsx';
import { BubblePlot } from '../components/charts/advanced.jsx';
import { fmt, fmtCr, fmtInt, bandColor } from '../lib/format.js';
import { generatePartnerScorecard } from '../lib/partnerScorecard.js';
import { FileDown } from 'lucide-react';

export default function Partners() {
  const { data, agg: A } = useData();
  if (!data || !A) return null;

  const downloadScorecard = (partnerName) => {
    try {
      generatePartnerScorecard(partnerName, data.allProjects, data.meta);
    } catch (e) {
      console.error('Scorecard generation failed:', e);
      alert(`Could not generate scorecard: ${e.message}`);
    }
  };

  // Min-2-project partners for top/lowest KPIs
  const min2 = A.partners.filter((p) => p.projects >= 2);
  const top = min2[0];
  const lowest = min2[min2.length - 1];

  const barsSet = min2.slice(0, 12);
  const impactSet = min2.slice(0, 14);

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-partner">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        <KpiCard testid="kpi-partner-count" hero label="Implementing Partners" value={fmtInt(A.partnersCount)} sub={`${min2.length} with 2+ projects`} />
        <PartnerKpi testid="kpi-partner-top" label="Top Performer" p={top} />
        <PartnerKpi testid="kpi-partner-low" label="Lowest Performer" p={lowest} />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" note="Min 2 projects per partner" />}>Partners Ranked · min 2 projects · top 12</SectionTitle>
        <Lollipop
          testid="partners-lollipop"
          rows={barsSet.map((p) => ({
            label: p.partner,
            value: p.avgScore,
            color: bandColor(p.band),
            sub: `${p.projects} · ${fmtCr(p.budgetCr)}`,
          }))}
        />
      </div>

      {/* Budget × Score bubble plot */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', gap: 10, fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600, flexWrap: 'wrap', alignItems: 'center' }}>
            <ProvenanceBadge kind="live" />
            {['Excellent', 'Good', 'Moderate', 'Poor'].map((b) => (
              <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 10, height: 10, background: bandColor(b), borderRadius: 50 }} />{b}
              </div>
            ))}
            <span style={{ color: 'var(--ink-4)', marginLeft: 4 }}>bubble size = # projects</span>
          </div>
        }>
          Partner Portfolio · Budget × Score, sized by project count
        </SectionTitle>
        <BubblePlot
          testid="partner-bubbles"
          points={min2.map((p) => ({
            name: p.partner,
            x: p.budgetCr || 0,
            y: p.avgScore || 0,
            r: p.projects,
            band: p.band,
            sub: `${p.type} · ${p.states.slice(0, 2).join(', ')}`,
          }))}
          xLabel="Total budget across partner's projects (₹ Cr)"
          yLabel="Average OECD overall score"
        />
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-4)' }}>
          Board question this answers — <b style={{ color: 'var(--ink-3)' }}>who spends more but scores less?</b> Bubbles in the lower-right quadrant.
        </div>

        <WatchList partners={min2} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>Impact & Sustainability Focus</SectionTitle>
          <table className="pv-table">
            <thead>
              <tr><th>Partner</th><th style={{ textAlign: 'right' }}>Prj</th><th style={{ textAlign: 'right' }}>Impact</th><th style={{ textAlign: 'right' }}>Sustain.</th><th style={{ textAlign: 'right' }}>Overall</th></tr>
            </thead>
            <tbody>
              {impactSet.map((p) => (
                <tr key={p.partner}>
                  <td style={{ fontSize: 12 }}>{p.partner}</td>
                  <td className="tabular" style={{ textAlign: 'right' }}>{p.projects}</td>
                  <td style={{ textAlign: 'right' }}><ScoreInline score={p.impact} /></td>
                  <td style={{ textAlign: 'right' }}><ScoreInline score={p.sustainability} /></td>
                  <td style={{ textAlign: 'right' }}><ScoreInline score={p.avgScore} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" note={`${A.partners.length} partners`} />}>Full Partner Summary</SectionTitle>
          <div style={{ marginTop: -4, marginBottom: 8, fontSize: 11, color: 'var(--ink-3)' }}>
            Click <FileDown size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> to download an auto-generated 1-page OECD scorecard PDF for that partner.
          </div>
          <div className="scroll-y" style={{ maxHeight: 480 }}>
            <table className="pv-table">
              <thead>
                <tr>
                  <th>#</th><th>Partner</th><th>Type</th><th>States</th>
                  <th style={{ textAlign: 'right' }}>Prj</th>
                  <th style={{ textAlign: 'right' }}>Budget</th>
                  <th>Score</th><th>Band</th>
                  <th style={{ textAlign: 'center' }}>PDF</th>
                </tr>
              </thead>
              <tbody>
                {A.partners.map((p, i) => (
                  <tr key={p.partner}>
                    <td className="tabular" style={{ color: 'var(--ink-4)' }}>{i + 1}</td>
                    <td style={{ fontSize: 12 }}>{p.partner}</td>
                    <td><Chip color={p.type === 'HRDP' ? '#042a54' : '#9b2d20'}>{p.type}</Chip></td>
                    <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.states.join(', ') || '—'}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{p.projects}</td>
                    <td className="tabular nowrap" style={{ textAlign: 'right' }}>{fmtCr(p.budgetCr)}</td>
                    <td><DataBarCell value={p.avgScore} /></td>
                    <td><BandPill band={p.band} /></td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => downloadScorecard(p.partner)}
                        data-testid={`scorecard-btn-${i}`}
                        title={`Download 1-page scorecard PDF for ${p.partner}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 24, height: 24, borderRadius: 6,
                          background: 'var(--primary-soft)', border: '1px solid var(--primary)',
                          color: 'var(--primary)', cursor: 'pointer',
                        }}
                      >
                        <FileDown size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ProvenanceFooter text="Top / lowest partner KPIs use a minimum-2-project threshold to avoid single-project outliers. Impact and Sustainability are per-partner averages of their project scores." />
    </div>
  );
}

function ScoreInline({ score }) {
  if (score === null || score === undefined) return <span style={{ color: 'var(--ink-4)' }}>—</span>;
  const b = score >= 4.6 ? 'Excellent' : score >= 3.6 ? 'Good' : score >= 2.6 ? 'Moderate' : 'Poor';
  return <span className="tabular" style={{ fontWeight: 700, color: bandColor(b) }}>{fmt(score, 2)}</span>;
}

function PartnerKpi({ label, p, testid }) {
  if (!p) return null;
  return (
    <div className="kpi-card fade-up" data-testid={testid}>
      <div className="kpi-label">{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{p.partner}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
        <span className="tabular" style={{ fontSize: 22, fontWeight: 800, color: bandColor(p.band) }}>{fmt(p.avgScore, 2)}</span>
        <BandPill band={p.band} />
      </div>
      <div className="kpi-sub">{p.projects} projects · {p.states.join(', ')}</div>
    </div>
  );
}

/**
 * WatchList — auto-listed partners in the "high spend / low score" quadrant.
 * Rules:
 *  - budget ≥ programme median partner-budget (upper half)
 *  - avgScore < 3.60 (below Good band)
 * Ranks by lowest score, ties broken by highest budget. Top 3.
 */
function WatchList({ partners }) {
  const funded = partners.filter((p) => p.budgetCr && p.avgScore);
  if (funded.length < 5) return null;
  const budgets = [...funded.map((p) => p.budgetCr)].sort((a, b) => a - b);
  const median = budgets[Math.floor(budgets.length / 2)];
  const watch = funded
    .filter((p) => p.budgetCr >= median && p.avgScore < 3.6)
    .sort((a, b) => (a.avgScore - b.avgScore) || (b.budgetCr - a.budgetCr))
    .slice(0, 3);

  if (watch.length === 0) {
    return (
      <div style={{
        marginTop: 12, padding: '10px 14px',
        background: '#1f8a4c11', border: '1px solid #1f8a4c33',
        borderLeft: '3px solid #1f8a4c', borderRadius: 8,
        fontSize: 12.5, color: '#166b3c', fontWeight: 600,
      }} data-testid="watch-list-empty">
        ✓ No partner in the high-spend / low-score quadrant. Portfolio looks efficient.
      </div>
    );
  }

  return (
    <div style={{
      marginTop: 14, padding: 14,
      background: 'linear-gradient(120deg, #fbe7e3, #ffffff 65%)',
      border: '1px solid #e23d2833', borderLeft: '4px solid #e23d28',
      borderRadius: 10,
    }} data-testid="watch-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: '#b62e1c', textTransform: 'uppercase', letterSpacing: '.09em' }}>
            Board watch-list
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginTop: 2 }}>
            High spend, low return — {watch.length} partner{watch.length === 1 ? '' : 's'} to review
          </div>
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--ink-4)', fontWeight: 600 }}>
          criteria: budget ≥ ₹{fmt(median, 1)} Cr · score &lt; 3.60
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {watch.map((p, i) => {
          const gap = 3.6 - p.avgScore;
          return (
            <div key={p.partner} style={{
              display: 'grid', gridTemplateColumns: '28px 1fr auto auto auto',
              gap: 10, alignItems: 'center',
              padding: '10px 12px', background: 'var(--surface)',
              border: '1px solid #e23d2833', borderRadius: 8,
            }} data-testid={`watch-row-${i}`}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: '#e23d28', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 800,
              }} className="tabular">{i + 1}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.partner}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {p.type} · {p.projects} project{p.projects === 1 ? '' : 's'} · {p.states.slice(0, 2).join(', ')}{p.states.length > 2 ? ` +${p.states.length - 2}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 700 }}>SPEND</div>
                <div className="tabular" style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)' }}>{fmtCr(p.budgetCr)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 700 }}>SCORE</div>
                <div className="tabular" style={{ fontSize: 13, fontWeight: 800, color: bandColor(p.band) }}>{fmt(p.avgScore, 2)}</div>
              </div>
              <div style={{ textAlign: 'right', minWidth: 68 }}>
                <div style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 700 }}>GAP</div>
                <div className="tabular" style={{ fontSize: 13, fontWeight: 800, color: '#c8452f' }}>−{fmt(gap, 2)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-4)' }}>
        <b style={{ color: 'var(--ink-3)' }}>Recommended action:</b> commission a partner-level review, adjust FY25-26 disbursement, or pair with a top-performing peer for capacity uplift.
      </div>
    </div>
  );
}
